import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Plus, X, Loader2, Accessibility, Baby } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLocation as useUserLocation } from "@/context/LocationContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const KINDS = ["ladies","gents","unisex","accessible","family"];

export default function Washrooms() {
  const { user } = useAuth();
  const { loc, detect } = useUserLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kind, setKind] = useState("");
  const [showForm, setShowForm] = useState(false);
  useSeo({ title:"Washrooms near me · Ladies · Gents · Unisex · find2near",
           description:"Community-mapped washrooms — Ladies, Gents, Unisex, Accessible, Family. Free & paid, 24×7 flagged, distance sorted.", canonical:"/washrooms" });

  const load = () => {
    setLoading(true);
    const p = { limit:60 };
    if (kind) p.kind = kind;
    if (loc.lat && loc.lng) { p.lat=loc.lat; p.lng=loc.lng; p.radius_km=5; }
    api.get("/washrooms",{params:p}).then(r=>setItems(r.data)).catch(()=>setItems([])).finally(()=>setLoading(false));
  };
  useEffect(load, [kind, loc.lat, loc.lng]);   // eslint-disable-line

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6" data-testid="washrooms-page">
      {showForm && <WrForm onClose={()=>setShowForm(false)} onSaved={()=>{setShowForm(false); load();}} />}
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold">🚽 Washrooms near me</div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Always know <span className="text-teal-600">where to go.</span></h1>
        </div>
        {user && <Button onClick={()=>setShowForm(true)} className="rounded-full bg-teal-600 hover:bg-teal-700 font-black"><Plus size={14} className="mr-1.5"/> Add washroom</Button>}
      </div>
      <div className="bg-white border border-stone-200 rounded-3xl p-3 flex flex-wrap gap-2 items-center mb-4">
        <Select value={kind||"__all__"} onValueChange={v=>setKind(v==="__all__"?"":v)}>
          <SelectTrigger className="rounded-full h-9 w-36 text-xs"><SelectValue placeholder="All types"/></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All types</SelectItem>{KINDS.map(k=><SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={()=>detect()} className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 rounded-full px-3 h-9"><MapPin size={12} className="inline mr-1"/> My location</button>
      </div>
      {loading ? <div className="text-center py-16 text-stone-400"><Loader2 className="mx-auto animate-spin"/></div>
      : items.length===0 ? <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-3xl">No washrooms yet — {user ? "add the first one!" : "sign in to add."}</div>
      : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {items.map(w=>(
            <div key={w.washroom_id} className="rounded-3xl bg-white border border-stone-200 p-4">
              <div className="flex items-center gap-1 flex-wrap mb-1">
                {w.kinds.map(k=><span key={k} className="text-[10px] font-black uppercase tracking-widest rounded-full px-2 py-0.5 bg-teal-100 text-teal-800 capitalize">{k}</span>)}
                {w.accessible && <Accessibility size={12} className="text-sky-600"/>}
                {w.baby_change && <Baby size={12} className="text-pink-600"/>}
                {w.distance_km != null && <span className="text-[10px] text-stone-500 ml-auto">{w.distance_km} km</span>}
              </div>
              <div className="font-black text-stone-900">{w.name}</div>
              <div className="text-xs text-stone-600">{w.is_free ? <span className="text-emerald-700 font-bold">Free</span> : (w.fee_note || "Paid")}{w.open_24x7 && <> · <b>24×7</b></>}</div>
              {w.address && <div className="text-xs text-stone-500 mt-1 line-clamp-2">{w.address}</div>}
              {w.location?.lat && <a href={`https://www.google.com/maps?q=${w.location.lat},${w.location.lng}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex text-[11px] font-bold text-teal-700 hover:underline"><MapPin size={11}/> Open in maps</a>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WrForm({ onClose, onSaved }) {
  const [f, setF] = useState({ name:"", kinds:["unisex"], is_free:true, fee_note:"", accessible:false, baby_change:false, address:"", city:"", lat:"", lng:"", open_24x7:false, notes:"" });
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setF({...f, kinds: f.kinds.includes(k) ? f.kinds.filter(x=>x!==k) : [...f.kinds, k]});
  const submit = async () => {
    if (f.name.trim().length < 2) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post("/washrooms", { ...f, lat: f.lat===""?null:parseFloat(f.lat), lng: f.lng===""?null:parseFloat(f.lng) });
      toast.success("Added"); onSaved?.();
    } catch (e) { toast.error(e?.response?.data?.detail||"Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200"><b>Add washroom</b><button onClick={onClose}><X size={18} className="text-stone-500"/></button></div>
        <div className="p-5 space-y-2 max-h-[75vh] overflow-y-auto">
          <Input value={f.name} onChange={e=>setF({...f,name:e.target.value})} placeholder="Landmark / building" className="rounded-2xl h-10"/>
          <div className="flex flex-wrap gap-1">{KINDS.map(k=><button key={k} type="button" onClick={()=>toggle(k)} className={`text-[11px] font-black rounded-full px-3 py-1 border capitalize ${f.kinds.includes(k)?"bg-teal-600 text-white border-teal-600":"bg-white text-stone-700 border-stone-200"}`}>{k}</button>)}</div>
          <Input value={f.address} onChange={e=>setF({...f,address:e.target.value})} placeholder="Address" className="rounded-2xl h-10"/>
          <div className="grid grid-cols-4 gap-2">
            <Input value={f.city} onChange={e=>setF({...f,city:e.target.value})} placeholder="City" className="rounded-2xl h-10 col-span-2"/>
            <Input value={f.lat} onChange={e=>setF({...f,lat:e.target.value})} placeholder="Lat" className="rounded-2xl h-10 font-mono"/>
            <Input value={f.lng} onChange={e=>setF({...f,lng:e.target.value})} placeholder="Lng" className="rounded-2xl h-10 font-mono"/>
          </div>
          <div className="flex gap-3 text-sm flex-wrap">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={f.is_free} onChange={e=>setF({...f,is_free:e.target.checked})}/> Free</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={f.accessible} onChange={e=>setF({...f,accessible:e.target.checked})}/> Accessible</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={f.baby_change} onChange={e=>setF({...f,baby_change:e.target.checked})}/> Baby-change</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={f.open_24x7} onChange={e=>setF({...f,open_24x7:e.target.checked})}/> 24×7</label>
          </div>
          <Button onClick={submit} disabled={busy} className="w-full rounded-full bg-teal-600 hover:bg-teal-700 font-black">{busy && <Loader2 className="animate-spin mr-2" size={14}/>} Add</Button>
        </div>
      </div>
    </div>
  );
}
